const BOOT_TEASER_ID = 'ecp-boot-teaser'
// The boot teaser's fixed geometry in index.html only matches the mobile layout.
const BOOT_TEASER_MEDIA = '(max-width: 639px)'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object'

const asPages = (body: unknown): Record<string, unknown>[] => {
  if (Array.isArray(body)) return body.filter(isRecord)

  const wrapped = isRecord(body) ? body.data : undefined

  if (!Array.isArray(wrapped)) return []

  return wrapped.filter(isRecord)
}

const pagePath = (page: Record<string, unknown>): string => {
  const slug = typeof page.slug === 'string' ? page.slug : ''

  if (slug === '' || slug === '/') return '/'

  return `/${slug.replace(/^\/+/, '')}`
}

const teaserUrl = (block: Record<string, unknown>): string | undefined => {
  const props = isRecord(block.props) ? block.props : undefined
  const content = props && isRecord(props.content) ? props.content : undefined
  const url = content?.url

  return typeof url === 'string' && url.length > 0 ? url : undefined
}

const readStoredIdToken = (storage: Storage): string | undefined => {
  let lastUserKey: string | undefined

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)

    if (
      key?.startsWith('CognitoIdentityServiceProvider.') &&
      key.endsWith('.LastAuthUser')
    ) {
      lastUserKey = key
      break
    }
  }

  if (!lastUserKey) return undefined

  const user = storage.getItem(lastUserKey)

  if (!user) return undefined

  const prefix = lastUserKey.slice(0, -'.LastAuthUser'.length)
  const token = storage.getItem(`${prefix}.${user}.idToken`)

  return token && token.length > 0 ? token : undefined
}

// Storage access throws where the browser blocks it (sandboxed iframes, blocked site data).
export const readCognitoIdToken = (storage?: Storage): string | undefined => {
  try {
    return readStoredIdToken(storage ?? localStorage)
  } catch {
    return undefined
  }
}

export const readEntryTeaserUrl = (
  body: unknown,
  pathname: string
): string | undefined => {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname
  const page = asPages(body).find((entry) => pagePath(entry) === normalized)

  if (!page || typeof page.id !== 'string') return undefined

  const blocks = page.blocks
  const list = Array.isArray(blocks)
    ? blocks.filter(isRecord)
    : Object.values(isRecord(blocks) ? blocks : {}).filter(isRecord)

  const match = list
    .filter((block) => {
      if (block.type !== 'teaser' || block.section !== 'main') return false
      if (block.parentId !== page.id) return false

      return teaserUrl(block) !== undefined
    })
    .sort(
      (left, right) =>
        (typeof left.order === 'number' ? left.order : 0) -
        (typeof right.order === 'number' ? right.order : 0)
    )[0]

  return match ? teaserUrl(match) : undefined
}

export const paintEntryTeaser = (url: string, doc: Document = document) => {
  if (doc.getElementById(BOOT_TEASER_ID)) return
  if (!doc.defaultView?.matchMedia(BOOT_TEASER_MEDIA).matches) return

  const img = doc.createElement('img')

  img.id = BOOT_TEASER_ID
  img.alt = ''
  img.width = 380
  img.height = 288
  img.setAttribute('fetchpriority', 'high')
  img.src = url
  doc.body.appendChild(img)
}

export const dismissEntryTeaser = (doc: Document = document) => {
  doc.getElementById(BOOT_TEASER_ID)?.remove()
}

export const waitForBootTeaser = (
  doc: Document = document,
  timeoutMs = 8000
): Promise<void> =>
  new Promise((resolve) => {
    const img = doc.getElementById(BOOT_TEASER_ID)

    if (!(img instanceof HTMLImageElement) || img.complete) {
      resolve()

      return
    }

    const view = doc.defaultView
    const timer = view?.setTimeout(() => resolve(), timeoutMs)
    const finish = () => {
      if (timer !== undefined) view?.clearTimeout(timer)
      resolve()
    }

    img.addEventListener('load', finish, { once: true })
    img.addEventListener('error', finish, { once: true })
  })
